/**
 * Game analysis orchestrator — coordinates the three Stockfish passes
 * and assembles the final result. Sub-modules handle each concern:
 *
 *   pgn-parser.ts      — PGN → MoveData[] (clock extraction)
 *   analysis-utils.ts  — uciListToSan, computeContinuationEvals
 *   accuracy.ts        — Lichess-derived move accuracy, Chessla game aggregation
 *   motif-tagger.ts    — per-move quality/error/context tags
 *   types.ts           — shared interfaces
 */
import { isCheckmate, sanForUci } from "@chessla/shared";
import type { AnalysisPreset } from "./analysis-presets";
import type { PositionEval, StockfishWorker } from "./stockfish-worker";
import type { AnalyzedMove, AnalysisProgress, ComputedGameAnalysis } from "./types";
import { parsePgnMoves } from "./pgn-parser";
import { computeContinuationEvals, legacyContinuationEvals, uciListToSan } from "./analysis-utils";
import { computeGameAccuracy } from "./accuracy";
import { deriveMotifs } from "./motif-tagger";
import { buildPrincipleSignals } from "./principle-signals";
import { BLUNDER_THRESHOLD_CP, MISTAKE_THRESHOLD_CP, IMPRECISION_THRESHOLD_CP } from "./constants";
import { estimateRatingFromCpl } from "./rating-estimate";

// Re-export types so callers only need to import from game-analyzer
export type { AnalyzedMove, AnalysisProgress, ComputedGameAnalysis } from "./types";

const CHECKMATE_EVALUATION: PositionEval = {
  cp: -10_000,
  mate: -1,
  bestMove: "",
  pv: [],
  depth: 0,
  nodes: 0,
  pvLines: [],
};

export const terminalCheckmateEvaluation = (fen: string): PositionEval | null =>
  isCheckmate(fen) ? CHECKMATE_EVALUATION : null;

const MAX_STORED_CONTINUATION_PLIES = 16;

export const storedContinuationSan = (fen: string, pv: string[]): string[] =>
  uciListToSan(fen, pv.slice(0, MAX_STORED_CONTINUATION_PLIES + 1)).slice(1);

export async function analyzeGame(
  engine: Pick<StockfishWorker, "evaluate">,
  pgn: string,
  playerColor: "w" | "b",
  gameId: string,
  preset: AnalysisPreset,
  onProgress?: (progress: AnalysisProgress) => void,
): Promise<ComputedGameAnalysis> {
  const movesData = parsePgnMoves(pgn, playerColor);
  const totalMoves = movesData.length;
  const analyzedMoves: AnalyzedMove[] = [];

  // ── Pass 1: evaluate EVERY move (both players) at MultiPV 1 ──────────
  // We now analyze opponent moves too, enabling detection of:
  // - Opponent blunders the player missed
  // - Threats the player defended against (or failed to defend)
  // - Critical moments where either player had winning chances
  const pass1StartTime = Date.now();
  let previousEvaluation: PositionEval | null = null;
  for (let i = 0; i < totalMoves; i++) {
    const m = movesData[i];

    // Analyze ALL moves now, not just player moves
    // The previous after-position is this before-position at the same depth.
    const evalBefore: PositionEval = previousEvaluation ?? (await engine.evaluate(m.fenBefore, preset.depth));
    // Stockfish has no legal reply in a checkmate position and can emit a
    // bare `bestmove` without an info score. The resulting board position is
    // definitive, so use a synthetic losing mate score for the side to move.
    const terminalEvaluation = terminalCheckmateEvaluation(m.fenAfter);
    const moveDeliversCheckmate = terminalEvaluation !== null;
    const evalAfter = terminalEvaluation ?? (await engine.evaluate(m.fenAfter, preset.depth));
    previousEvaluation = evalAfter;

    const evalBeforeCp = evalBefore.cp;
    const evalAfterCp = evalAfter.cp;
    const nodes = evalBefore.nodes;

    onProgress?.({
      currentMove: i + 1,
      totalMoves,
      phase: "analyzing",
      currentSan: m.san,
      nodes,
      depth: evalBefore.depth,
      startTime: pass1StartTime,
      phaseName: "Analyzing moves",
    });

    let bestMoveSan: string | null = null;
    let bestMoveFrom: string | null = null;
    let bestMoveTo: string | null = null;
    let bestContinuation: string[] = [];

    if (evalBefore.bestMove) {
      bestMoveFrom = evalBefore.bestMove.slice(0, 2);
      bestMoveTo = evalBefore.bestMove.slice(2, 4);
      try {
        bestMoveSan = sanForUci(m.fenBefore, evalBefore.bestMove) ?? "";
      } catch {
        /* ignore */
      }
      if (evalBefore.pv.length > 1) {
        bestContinuation = storedContinuationSan(m.fenBefore, evalBefore.pv);
      }
    }

    // Search variation between positions is not a loss for playing the exact
    // recommended move. Preserve both raw scores independently of move regret.
    const centipawnLoss =
      m.playedUci === evalBefore.bestMove || moveDeliversCheckmate
        ? 0
        : evalBeforeCp != null && evalAfterCp != null
          ? Math.max(0, evalBeforeCp + evalAfterCp)
          : 0;

    analyzedMoves.push({
      moveNumber: m.moveNumber,
      san: m.san,
      fenBefore: m.fenBefore,
      fenAfter: m.fenAfter,
      isPlayerMove: m.isPlayerMove,
      evalBeforeCp,
      evalAfterCp,
      evalBeforeMate: evalBefore.mate,
      evalAfterMate: evalAfter.mate,
      centipawnLoss,
      // Only classify errors for player moves (coaching focus)
      isBlunder: !moveDeliversCheckmate && m.isPlayerMove && centipawnLoss >= BLUNDER_THRESHOLD_CP,
      isMistake:
        !moveDeliversCheckmate &&
        m.isPlayerMove &&
        centipawnLoss >= MISTAKE_THRESHOLD_CP &&
        centipawnLoss < BLUNDER_THRESHOLD_CP,
      isImprecision:
        !moveDeliversCheckmate &&
        m.isPlayerMove &&
        centipawnLoss >= IMPRECISION_THRESHOLD_CP &&
        centipawnLoss < MISTAKE_THRESHOLD_CP,
      bestMoveSan,
      bestMoveFrom,
      bestMoveTo,
      playedFrom: m.playedFrom,
      playedTo: m.playedTo,
      bestContinuation,
      bestContinuationEvals: null,
      bestContinuationScores: null,
      tacticalVerification: null,
      // Mark as critical if EITHER player made a significant error
      isCriticalPosition: centipawnLoss >= IMPRECISION_THRESHOLD_CP,
      nodes,
      alternatives: [],
      clockSeconds: m.clockSeconds,
      timeSpentSeconds: m.timeSpentSeconds,
      motifs: [],
      principleSignals: [],
    });
  }

  // ── Pass 2: selectively recheck critical positions ─────────────────────
  // Deep investigates every imprecision-or-worse position. Regular keeps the
  // full-game baseline but spends follow-up work only on meaningful mistakes
  // by the player; this is what makes it a practical everyday analysis.
  const flagged = analyzedMoves.filter((m) => m.centipawnLoss >= IMPRECISION_THRESHOLD_CP);
  const followUpMoves =
    preset.id === "deep" ? flagged : flagged.filter((m) => m.isPlayerMove && m.centipawnLoss >= MISTAKE_THRESHOLD_CP);
  const startTime = Date.now();
  for (let i = 0; i < followUpMoves.length; i++) {
    const m = followUpMoves[i];
    const multiPvToUse = preset.id === "deep" ? preset.multiPv : 1;

    const rerun = await engine.evaluate(m.fenBefore, preset.depth, multiPvToUse > 1 ? multiPvToUse : undefined);
    if (m.isPlayerMove && m.centipawnLoss >= MISTAKE_THRESHOLD_CP) {
      m.tacticalVerification = {
        version: 2,
        first: {
          depth: rerun.depth,
          cp: rerun.cp,
          mate: rerun.mate,
          bestMoveUci: rerun.bestMove,
        },
        second: {
          depth: rerun.depth,
          cp: rerun.cp,
          mate: rerun.mate,
          bestMoveUci: rerun.bestMove,
        },
      };
    }

    onProgress?.({
      currentMove: i + 1,
      totalMoves: followUpMoves.length,
      phase: "alternatives",
      currentSan: m.san,
      nodes: rerun.nodes,
      startTime,
      phaseName: "Finding alternatives for mistakes",
    });
    m.alternatives = rerun.pvLines
      .filter((pv) => pv.rank > 1)
      .map((pv) => {
        let san = pv.moveUci;
        const from = pv.moveUci.slice(0, 2);
        const to = pv.moveUci.slice(2, 4);
        try {
          san = sanForUci(m.fenBefore, pv.moveUci) ?? "";
        } catch {
          /* ignore */
        }
        return { san, from, to, cp: pv.cp };
      });
  }

  // ── Pass 3: prove selected mistakes with objective engine evidence ──────
  const pass3StartTime = Date.now();
  for (let i = 0; i < followUpMoves.length; i++) {
    const m = followUpMoves[i];
    const engineContinuation = m.bestContinuation.slice(0, 2);
    onProgress?.({
      currentMove: i + 1,
      totalMoves: followUpMoves.length,
      phase: "continuations",
      currentSan: m.bestMoveSan ?? m.san,
      startTime: pass3StartTime,
      phaseName: "Computing best lines",
    });
    m.bestContinuation = engineContinuation;
    if (m.bestMoveSan && m.bestContinuation.length > 0) {
      m.bestContinuationScores = await computeContinuationEvals(
        engine,
        m.fenBefore,
        m.bestMoveSan,
        m.bestContinuation,
        preset.continuationDepth,
        Math.max(10, preset.depth - 2),
      );
      m.bestContinuationEvals = legacyContinuationEvals(m.bestContinuationScores);
    }
    if (m.tacticalVerification != null) {
      // Regular's confirmation reaches depth 18; Deep keeps its stronger
      // depth-26 check. This stable pair is enough to retain a useful engine
      // opportunity even when the longer proof line is incomplete.
      const stronger = await engine.evaluate(m.fenBefore, preset.depth + 2, 1);
      m.tacticalVerification.second = {
        depth: stronger.depth,
        cp: stronger.cp,
        mate: stronger.mate,
        bestMoveUci: stronger.bestMove,
      };
    }
  }

  // Reset engine to MultiPV 1 for the next game in the batch
  await engine
    .evaluate(movesData[0]?.fenBefore ?? "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1", 1, 1)
    .catch(() => {});

  // ── Summary stats ─────────────────────────────────────────────────────
  // Stats focus on player moves only (coaching target), even though we now
  // have eval data for opponent moves too (used for threat detection, etc.)
  const playerMoves = analyzedMoves.filter((m) => m.isPlayerMove);
  const totalCpl = playerMoves.reduce((sum, m) => sum + m.centipawnLoss, 0);
  const averageCpl = playerMoves.length > 0 ? totalCpl / playerMoves.length : 0;
  const blunderCount = playerMoves.filter((m) => m.isBlunder).length;
  const mistakeCount = playerMoves.filter((m) => m.isMistake).length;
  const accuracy = computeGameAccuracy(playerMoves);

  // Derive motifs after all evals are final
  // Now we derive motifs for ALL moves (both players) since we have eval data
  // for everything. This enables detecting opponent threats, missed opportunities, etc.
  for (const m of analyzedMoves) {
    m.motifs = deriveMotifs(m);
  }
  const movesWithPrinciples = buildPrincipleSignals(analyzedMoves);

  onProgress?.({ currentMove: totalMoves, totalMoves, phase: "done" });

  return {
    gameId,
    averageCpl,
    blunderCount,
    mistakeCount,
    accuracy,
    estimatedRating: estimateRatingFromCpl(averageCpl),
    moves: movesWithPrinciples,
  };
}
